import Axios, { type AxiosInstance, type AxiosPromise } from 'axios';
import {
	type AemBookingDay,
	type ITimeSlot,
	type ITourFormData
} from 'Types/aemui/schedule-a-tour/scheduleATour';
import {
	type IBookingsServiceResponse,
	type IBookingsStaffAvailabilityResponse,
	BookingAppointmentRequest
} from 'Types/bookings/bookings';
import dayjs from 'dayjs';
import { bookingsTokenService } from './bookingsTokenService';
import { club } from '@/util/club';
import { getAvailableTimeSlots } from '@/util/timeSlotAvailability';

export const BookingsServiceAxiosInstance: AxiosInstance = Axios.create({
	baseURL: 'https://graph.microsoft.com/v1.0/solutions/bookingBusinesses'
});

class BookingsService {
	axiosInstance: AxiosInstance = BookingsServiceAxiosInstance;
	async getClubTourService(
		token: string,
		bookingsBusiness: string
	): AxiosPromise<IBookingsServiceResponse> {
		const authHeader = {
			Authorization: `Bearer ${token}`
		};

		const params = getServiceSearchParams();

		return this.axiosInstance.get(`/${bookingsBusiness}/services`, {
			headers: authHeader,
			params
		});
	}

	async getServiceAvailability(
		token: string,
		bookingsBusiness: string,
		staffId: string[]
	): AxiosPromise<IBookingsStaffAvailabilityResponse> {
		const authHeader = {
			Authorization: `Bearer ${token}`
		};
		// May need to add an additional call here in the future to allow these to be variable.
		// For now I've been assured every calendar is the same.
		let startDate = dayjs().add(2, 'hour'); // 2 hour lead time

		if (startDate.minute() > 0) {
			startDate = startDate.add(1, 'hour');
			startDate = startDate.set('minute', 0);
		}
		const endDate = dayjs().add(14, 'day'); // 14 day window

		const request = {
			staffIds: staffId,
			startDateTime: {
				dateTime: startDate,
				timeZone: 'UTC'
			},
			endDateTime: {
				dateTime: endDate,
				timeZone: 'UTC'
			}
		};

		return this.axiosInstance.post(`/${bookingsBusiness}/getStaffAvailability`, request, {
			headers: authHeader
		});
	}

	async getStaffAvailability(): Promise<AemBookingDay[]> {
		try {
			// Paradise Valley opening 2026
			const bookingsBusiness =
				window.location.hostname !== 'www.lifetime.life'
					? 'ParadiseValley@ltfinc.onmicrosoft.com'
					: window.lt.club.smtpScheduleTour;

			const token = await bookingsTokenService.getToken();
			const services = await this.getClubTourService(token.data.access_token, bookingsBusiness);

			// Make call to staff availability and process it.
			const availability = await this.getServiceAvailability(
				token.data.access_token,
				bookingsBusiness,
				services.data.value[0].staffMemberIds
			);

			const actualResponse = getAvailableTimeSlots(
				services.data,
				availability.data,
				club.timeZone,
				bookingsBusiness
			);

			return actualResponse;
		} catch (error: unknown) {
			if (Axios.isAxiosError(error)) {
				throw error;
			}
			throw new Error('An unknown error was thrown');
		}
	}

	async submitBooking(timeSlot: ITimeSlot, formData: ITourFormData): AxiosPromise {
		let authHeader: { Authorization: string };
		let actualResponse: AemBookingDay[];

		try {
			const token = await bookingsTokenService.getToken();
			authHeader = {
				Authorization: `Bearer ${token.data.access_token}`
			};

			const services = await this.axiosInstance.get(`/${timeSlot.bookingsBusiness}/services`, {
				headers: authHeader,
				params: getServiceSearchParams()
			});

			const availability = await this.getServiceAvailability(
				token.data.access_token,
				timeSlot.bookingsBusiness,
				[timeSlot.staffId]
			);

			actualResponse = getAvailableTimeSlots(
				services.data,
				availability.data,
				timeSlot.timeZone,
				timeSlot.bookingsBusiness
			);
		} catch (error: unknown) {
			if (Axios.isAxiosError(error)) {
				throw error;
			}
			throw Error('Error retrieving current time slots');
		}

		const isStillAvailable = actualResponse.some((x) =>
			x.timeSlots.some((y) => dayjs(y.startTime).isSame(timeSlot.startTime, 'day'))
		);

		if (isStillAvailable) {
			return this.axiosInstance.post(
				`/${timeSlot.bookingsBusiness}/appointments`,
				new BookingAppointmentRequest(timeSlot, formData),
				{
					headers: authHeader
				}
			);
		} else {
			throw Error('Staff not available:');
		}
	}
}

function getServiceSearchParams(): URLSearchParams {
	const params = new URLSearchParams();
	params.append('$filter', "displayName eq 'Club Tour'");
	return params;
}

export const bookingsService = new BookingsService();
